import React from 'react';
import { FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TenantSelect } from '@/backoffice/features/tenants/components/tenant-select/tenant-select';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import { TPolicyEditorInputSchema } from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.types';
import { createIdResourceFilter } from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.utils';
import { TResourceSelectProps } from '@/shared/features/rbac/components/create-policy/resource-select/resource-select.types';
import { RoleSelect } from '@/shared/features/rbac/components/role-select/role-select';
import { UserAdditionalInfo } from '@/shared/features/rbac/components/user-select/user-additional-info/user-additional-info';
import { UserSelect } from '@/shared/features/rbac/components/user-select/user-select';
import {
  useWildCardOption,
  WILD_CARD,
} from '@/shared/features/rbac/hooks/use-wild-card-option/use-wild-card-option';
import { getFullname } from '@/shared/utils/format';

export const BackofficeResourceSelect: React.FC<TResourceSelectProps> = ({
  resourceType,
  policyIndex,
}) => {
  const { t } = useTranslation();
  const { handleWildCardSelect } = useWildCardOption();
  const valuePrefix = `${resourceType}:id`;
  const idResourceFilter = createIdResourceFilter(resourceType);
  const name =
    `policies.${policyIndex}.resource` as FieldPath<TPolicyEditorInputSchema>;

  const getWildCardOption = (asset: string): TSelectOption<string> => ({
    label: t('rbac:all_resources_of_type', {
      resourceType: t(`rbac:resource_type.${asset.toLowerCase()}`),
    }),
    value: `${asset}:${WILD_CARD}`,
  });

  if (resourceType === ResourceTypeEnum.BackofficeUser) {
    return (
      <UserSelect<TPolicyEditorInputSchema, string>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        multiple
        getItemId={(value) => value}
        createOption={(user) => ({
          label: getFullname(user),
          value: `${valuePrefix}:${user.userId}`,
          additionalInfo: <UserAdditionalInfo user={user} />,
        })}
        additionalOptions={[getWildCardOption(resourceType)]}
        required
      />
    );
  }

  if (resourceType === ResourceTypeEnum.BackofficeUserRole) {
    return (
      <RoleSelect<TPolicyEditorInputSchema, string>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(role) => ({
          label: role.name,
          value: `${valuePrefix}:${role.userRoleId}`,
        })}
        additionalOptions={[getWildCardOption(resourceType)]}
      />
    );
  }

  if (resourceType === ResourceTypeEnum.BackofficeTenant) {
    return (
      <TenantSelect<TPolicyEditorInputSchema>
        name={name}
        disableTags={false}
        enableTagsTooltip
        onSelectChange={handleWildCardSelect}
        filterSelectedTags={idResourceFilter}
        getItemId={(value) => value}
        createOption={(tenant) => ({
          label: tenant.companyName,
          value: `${valuePrefix}:${tenant.tenantId}`,
          additionalInfo: [
            tenant.owner?.email,
            t(`countries:${tenant.country}`),
            t(`industries:${tenant.industry}`),
          ],
        })}
        additionalOptions={[getWildCardOption(resourceType)]}
        required
      />
    );
  }

  return null;
};
